import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Employee {
  id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  location_id?: string;
  location_name?: string;
  preferred_languages?: string[];
}

interface EmployeeContextType {
  currentEmployee: Employee | null;
  setCurrentEmployee: (employee: Employee | null) => void;
  clearCurrentEmployee: () => void;
}

const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined);

interface EmployeeProviderProps {
  children: ReactNode;
}

export const EmployeeProvider: React.FC<EmployeeProviderProps> = ({ children }) => {
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);

  // Load employee from localStorage on mount
  useEffect(() => {
    const storedEmployee = localStorage.getItem('smartdry_current_employee');
    if (storedEmployee) {
      try {
        const employee = JSON.parse(storedEmployee);
        setCurrentEmployee(employee);
        console.log('🔧 Restored current employee from storage:', employee.first_name);
      } catch (error) {
        console.error('❌ Failed to parse stored employee:', error);
        localStorage.removeItem('smartdry_current_employee');
      }
    }
  }, []);

  // Save employee to localStorage whenever it changes
  useEffect(() => {
    if (currentEmployee) {
      localStorage.setItem('smartdry_current_employee', JSON.stringify(currentEmployee));
      console.log('💾 Saved current employee to storage:', currentEmployee.first_name);
    } else {
      localStorage.removeItem('smartdry_current_employee');
      console.log('🗑️ Cleared current employee from storage');
    }
  }, [currentEmployee]);

  const clearCurrentEmployee = () => {
    setCurrentEmployee(null);
  };

  return (
    <EmployeeContext.Provider value={{
      currentEmployee,
      setCurrentEmployee,
      clearCurrentEmployee
    }}>
      {children}
    </EmployeeContext.Provider>
  );
};

export const useEmployee = (): EmployeeContextType => {
  const context = useContext(EmployeeContext);
  if (context === undefined) {
    throw new Error('useEmployee must be used within an EmployeeProvider');
  }
  return context;
};
